import * as React from "react";
import {
  elementWidth,
  partHeight,
  hooksRectWidth,
  hooksRectHeight,
} from "./dimensions";

export const state = new Map([
  [
    "useState",
    {
      type: "function",
      metatype: "hook",
      description: "Lets you add a state variable to your component",
      scale: 1,
      return: [
        { name: "state", description: "The current state", type: "any" },
        {
          name: "setState",
          type: "function",
          description:
            "The set function that lets you update the state to a different value and trigger a re-render.",
          parameters: new Map([
            [
              "value",
              {
                name: "newState",
                type: "any",
                description:
                  "The value that you want the state to be. It can be a value of any type, but there is a special behavior for functions.",
              },
            ],
            [
              "function",
              {
                name: "stateUpdater",
                type: "function",
                metatype: "pure",

                return: {
                  name: "newState",
                  description: "The value that you want the state to be.",
                  type: "any",
                },
                parameters: [
                  {
                    name: "pendingState",
                    type: "any",
                  },
                ],
                description: (
                  <span>
                    If you pass a function as <code>nextState</code>, it will be
                    treated as an <dfn>updater function</dfn>. It must be pure,
                    should take the pending state as its only argument, and
                    should return the next state. React will put your updater
                    function in a queue and re-render your component. During the
                    next render, React will calculate the next state by applying
                    all of the queued updaters to the previous state
                  </span>
                ),
              },
            ],
          ]),
          return: null, // undefined
        },
      ], // add tooltips around the elements
      parameters: new Map([
        [
          "value",
          {
            name: "initialState",
            type: "any",
            description:
              "The value you want the state to be initially. It can be a value of any type, but there is a special behavior for functions. This argument is ignored after the initial render.",
          },
        ],
        [
          "function",
          {
            name: "stateInitializer",
            type: "function",
            metatype: "pure",
            displayValue: "() => initialState",
            description: (
              <span>
                If you pass a function as <code>initialState</code>, it will be
                treated as an <dfn>initializer function</dfn>. It should be
                pure, should take no arguments, and should return a value of any
                type. React will call your initializer function when
                initializing the component, and store its return value as the
                initial state.
              </span>
            ),
            parameters: [],
            return: {
              name: "initialState",
              type: "The value that you want the state to be. This argument is ignored after the initial render.",
            },
          },
        ],
      ]),
      docs: [
        {
          title: "Adding Interactivity",
          url: "https://beta.reactjs.org/learn/adding-interactivity",
        },
        {
          title: "Thinking in React",
          url: "https://beta.reactjs.org/learn/thinking-in-react#step-3-find-the-minimal-but-complete-representation-of-ui-state",
        },
        {
          title: "Managing State",
          url: "https://beta.reactjs.org/learn/managing-state",
        },
        {
          title: "Queueing a Series of State Updates",
          url: "https://beta.reactjs.org/learn/queueing-a-series-of-state-updates",
        },
        {
          title: "Updating Objects in State",
          url: "https://beta.reactjs.org/learn/updating-objects-in-state",
        },
        {
          title: "Updating Arrays in State",
          url: "/updating-objects-in-state",
        },
      ],
      recommended: [
        {
          title: "7 things you may not know about useState | Thoughtspile",
          url: "https://blog.thoughtspile.tech/2021/09/27/usestate-tricks/",
        },
        {
          title: "useState for one-time initializations | TkDodo’s blog",
          url: "https://tkdodo.eu/blog/use-state-for-one-time-initializations",
        },
        {
          title: "Things to know about useState | TkDodo’s blog",
          url: "https://tkdodo.eu/blog/things-to-know-about-use-state",
        },
        {
          title: "useState lazy initialization and function updates",
          url: "https://kentcdodds.com/blog/use-state-lazy-initialization-and-function-updates",
        },
      ],
    },
  ],
  [
    "useReducer",
    {
      type: "function",
      metatype: "hook",
      scale: 0.9,
      description: "Lets you manage a component’s state with a reducer.",
      return: [
        { name: "state", description: "The current state", type: "any" },
        {
          name: "dispatch",
          type: "function",
          parameters: [
            {
              name: "action",
              type: "any",
              description: (
                <span>
                  The action performed by the user. It can be a value of any
                  type. By convention, an action is usually an object with a{" "}
                  <code>type</code> property identifying it and, optionally,
                  other properties with additional information
                </span>
              ),
            },
          ],
          description:
            "The dispatch function that lets you update the state to a different value and trigger a re-render.",
          return: null,
        },
      ],
      parameters: [
        {
          name: "reducer",
          type: "function",
          metatype: "pure",
          description:
            "The reducer function that specifies how the state gets updated. It must be pure, should take the state and action as arguments, and should return the next state.",
          parameters: [
            { name: "state", type: "any", description: "The current state." },
            {
              name: "action",
              type: "any",
              description: (
                <span>
                  The <code>action</code> value passed to the{" "}
                  <code>useReducer</code>’s <code>dispatch</code> function.
                </span>
              ),
            },
          ],
          return: {
            name: "nextState",
            type: "any",
          },
        },
        {
          name: "initialArg",
          type: "any",
          description: "The value from which the initial state is calculated.",
        },
        {
          name: "init",
          type: "function",
          metatype: "pure",
          description: (
            <span>
              The initializer function that specifies how the initial state is
              calculated. If it’s not specified, the initial state is set to
              initialArg. Otherwise, the initial state is set to the result of
              calling <code>init(initialArg)</code>.
            </span>
          ),
          parameters: [
            {
              name: "initialArg",
              description: (
                <span>
                  The preceding argument passed to <code>useReducer</code>
                </span>
              ),
            }, // ideally would show an arrow pointing maxto the preceding initialArg param
          ],
          return: {
            name: "initialState",
            type: "any",
          },
          optional: true,
        },
      ],
      recommended: [
        {
          title:
            "A Complete Guide to useEffect: Why useReducer Is the Cheat Mode of Hooks",
          url: "https://overreacted.io/a-complete-guide-to-useeffect/#why-usereducer-is-the-cheat-mode-of-hooks)",
        },
        {
          title: "AdamReacts: Hooks, State, Closures, and useReducer",
          url: "https://adamrackis.dev/state-and-use-reducer/",
        },
        {
          title: "useState vs useReducer | TkDodo’s blog",
          url: "https://tkdodo.eu/blog/use-state-vs-use-reducer",
        },
        {
          title: "Actionless and Stateless Reducers in React - JulesBlom.com",
          url: "https://julesblom.com/writing/actionless-and-stateless-reducers",
        },
        {
          title: "Medium Reducers in React - JulesBlom.com",
          url: "https://julesblom.com/writing/medium-reducers",
        },
      ],
      docs: [
        {
          url: "https://beta.reactjs.org/learn/scaling-up-with-reducer-and-context",
          title: "Scaling Up with Reducer and Context",
        },
        {
          url: "https://beta.reactjs.org/learn/extracting-state-logic-into-a-reducer",
          title: "Extracting State Logic into a Reducer",
        },
      ],
      tweets: [
        {
          url: "https://twitter.com/sebmarkbage/status/1534184946742484997",
          author: "@sebmarkbage",
        },
      ],
    },
  ],
  [
    "useSyncExternalStore",
    {
      type: "function",
      metatype: "hook",
      scale: 0.5,
      description: "Lets you subscribe to an external store.",
      julesNote: (
        <span>
          <code>useSyncExternalStore</code> is mostly, but not fully a library
          hooks.
        </span>
      ),
      return: {
        name: "snapshot",
        type: "any",
        description: "the snapshot of the data in the store",
      },
      parameters: [
        {
          name: "subscribe",
          description:
            "A function that takes a single callback argument and subscribes it to the store. When the store changes, it should invoke the provided callback. This will cause the component to re-render. The subscribe function should return a function that cleans up the subscription.",
          type: "function",
          parameters: [
            {
              name: "callback",
              type: "function",
              description:
                "When the store changes, it should invoke the provided callback. This will cause the component to re-render.",
            },
          ],
          return: {
            name: "cleanupFn",
            type: "function",
            parameters: [],
            return: undefined,
          },
        },
        {
          name: "getSnapshot",
          type: "function",
          parameters: [],
          description:
            "A function that returns a snapshot of the data in the store that’s needed by the component.",
          return: {
            name: "snapshot",
            type: "any",
          },
        },
        {
          name: "getServerSnapshot",
          type: "function",
          optional: true,
          description:
            "An optional function that returns the initial snapshot of the data in the store. It will be used only during server rendering and during hydration of server-rendered content on the client.",
          parameters: [],
          return: {
            name: "initialSnapshot",
            type: "any",
          },
        },
      ],
      recommended: [
        {
          url: "https://thisweekinreact.com/articles/useSyncExternalStore-the-underrated-react-api",
          title:
            "useSyncExternalStore - The underrated React API | This Week In React",
        },
        {
          url: "https://julesblom.com/writing/usesyncexternalstore",
          title: "useSyncExternalStore First Look — JulesBlom.com",
        },
      ],
      tweets: [
        {
          url: "https://twitter.com/sebastienlorber/status/1615329010761842689",
          author: "@sebastienlorber",
        },
      ],
    },
  ],
]);

const stateHeight = state.size * partHeight + 10;
export const statePosition = {
  height: stateHeight,
  width: elementWidth,
  x: hooksRectWidth / 2 - elementWidth / 2,
  y: hooksRectHeight / 2 - stateHeight / 2,
};

export const suspense = new Map([
  [
    "useDeferredValue",
    {
      type: "function",
      metatype: "hook",
      scale: 0.3,
      description: "Lets you defer updating a part of the UI.",
      parameters: [
        {
          name: "value",
          type: "any",
          description: "The value you want to defer.",
        },
      ],
      recommended: [
        {
          title: "Hooks You Probably Don't Need — useDeferredValue",
          url: "https://reacttraining.com/blog/hooks-you-probably-dont-need#usedeferredvalue",
        },
      ],
      return: {
        name: "deferredValue",
        type: "any",
        description:
          "During the initial render, the returned deferred value will be the same as the value you provided. During updates, React will first attempt a re-render with the old value (so the returned value will match the old value), and then try another re-render in background with the new value (so the returned value will match the updated value).",
      },
    },
  ],
  [
    "useTransition",
    {
      type: "function",
      scale: 0.5,
      metatype: "hook",
      description: "Lets you update the state without blocking the UI",
      parameters: [],
      recommended: [
        {
          title:
            "React 18 Concurrent Mode: How transitions really work (feat Wordle) | Morten Barklund",
          url: "https://mortenbarklund.com/blog/react-18-concurrent-mode-how-transitions-work/",
        },
        {
          title: "AndreiGatej.dev: The underlying mechanisms of React’s concurrent mode",
          url: "https://andreigatej.dev/blog/the-underlying-mechanisms-of-reacts-concurrent-mode/",
        },
      ],
      return: [
        {
          name: "isPending",
          type: "boolean",
          description:
            "A flag that tells you whether there is a pending transition.",
        },
        {
          name: "startTransition",
          type: "function",
          description:
            "Function that lets you mark a state update as a transition.",
          return: undefined,
          parameters: [
            {
              name: "scope",
              type: "function",
              description: (
                <span>
                  A function that updates some state by calling one or more set
                  functions. React immediately calls <code>scope</code> with no
                  parameters and marks all state updates scheduled synchronously
                  during the scope function call as transitions. They will be
                  non-blocking and will not display unwanted loading indicators.
                </span>
              ),
            },
          ],
        },
      ],
    },
  ],
  [
    "startTransition",
    {
      type: "function",
      metatype: "api",
      scale: 0.5,
      description:
        "Function that lets you mark a state update as a transition.",
      return: undefined,
      recommended: [
        {
          title:
            "React 18 Concurrent Mode: How transitions really work (feat Wordle) | Morten Barklund",
          url: "https://mortenbarklund.com/blog/react-18-concurrent-mode-how-transitions-work/",
        },
      ],
      parameters: [
        {
          name: "scope",
          type: "function",
          description: (
            <span>
              A function that updates some state by calling one or more set
              functions. React immediately calls <code>scope</code> with no
              parameters and marks all state updates scheduled synchronously
              during the scope function call as transitions. They will be
              non-blocking and will not display unwanted loading indicators.
            </span>
          ),
        },
      ],
    },
  ],
  [
    "Suspense",
    {
      description:
        "Lets you display a fallback until its children have finished loading.",
      scale: 0.7,
      recommended: [
        {
          title:
            "elanmed.dev | React Suspense in three different architectures",
          url: "https://elanmed.dev/blog/suspense-in-different-architectures",
        },
      ],
      parameters: [
        {
          name: "children",
          metatype: "ReactNode",
          description: (
            <span>
              The actual UI you intend to render. If <code>children</code>{" "}
              suspends while rendering, the Suspense boundary will switch to
              rendering fallback.
            </span>
          ),
        },
        {
          name: "fallback",
          metatype: "ReactNode",
          value: "fallbackUI",
          description: (
            <span>
              An alternate UI to render in place of the actual UI if it has not
              finished loading. Any valid React node is accepted, though in
              practice, a fallback is a lightweight placeholder view, such as a
              loading spinner or skeleton. Suspense will automatically switch to
              fallback when <code>children</code> suspends, and back to children
              when the data is ready. If fallback suspends while rendering, it
              will activate the closest parent Suspense boundary.
            </span>
          ),
        },
      ],
      type: "component",
    },
  ],
  [
    "lazy",
    {
      type: "function",
      description:
        "Lets you defer loading component’s code until it is rendered for the first time.",
      metatype: "api",
      scale: 0.7,
      parameters: [
        {
          name: "load",
          type: "function",
          return: { type: "promise", name: "Component" },
          parameters: [],
          description: (
            <span>
              A function that returns a Promise or some other thenable (a
              Promise-like object with a <code>then</code> method). React will
              not call load until the first time you attempt to render the
              returned component. After React first calls load, it will wait for
              it to resolve, and then render the resolved value as a React
              component. Both the returned Promise and the Promise’s resolved
              value will be cached, so React will not call load more than once.
              If the Promise rejects, React will throw the rejection reason to
              let the closest Error Boundary above handle it.
            </span>
          ),
        },
      ],
      docs: [
        {
          title: "Code-Splitting — React",
          url: "https://reactjs.org/docs/code-splitting.html",
        },
      ],
      recommended: [
        {
          title: "Code splitting with React.lazy and Suspense",
          url: "https://web.dev/code-splitting-suspense/",
        },
      ],
      return: {
        description: "React component that you can render in your tree",
        type: "component",
        name: "LazyComponent",
      },
    },
  ],
]);
export const suspensePosition = {
  height: 220,
  width: elementWidth * 3,
  x: -elementWidth - 14,
  y: hooksRectHeight - 80,
};

export const nodes = new Map([
  [
    "Fragment",
    {
      type: "component",
      description: (
        <span>
          Fragments let lets you group elements without a wrapper node. It’s
          commonly used via the <code>&lt;&gt;...&lt;/&gt;</code> syntax. The
          only time explicit <code>&lt;Fragment&gt;</code> syntax is required is
          when you need to add a <code>key</code>.
        </span>
      ),
      recommended: [],
      scale: 1,
      parameters: [
        {
          name: "key",
          optional: true,
          type: ["string", "number"],
          value: "id",
          description: (
            <span>
              Fragments declared with the explicit <code>&lt;Fragment&gt;</code>{" "}
              syntax may have keys.
            </span>
          ),
        },
        {
          name: "children",
          description: "The elements you need to group together.",
        },
      ],
    },
  ],
  [
    "createElement",
    {
      type: "function",
      metatype: "api",
      description:
        "Lets you create a React element. It serves as an alternative to writing JSX.",
      scale: 0.5,
      parameters: [
        {
          name: "type",
          description:
            "The type argument must be a valid React component type.",
        },
        {
          name: "props",
          description: "The props argument must either be an object or null.",
        },
        {
          name: "...children",
          description: "Zero or more child nodes.",
          optional: true,
        },
      ],
      recommended: [],
      return: {
        name: "element",
        type: "object",
        metatype: "element",
        description:
          "An element is a lightweight description of a piece of the user interface. React elements are immutable. Once you create an element, you can’t change its children or attributes.",
        properties: [
          { name: "type", description: "The type you have passed" },
          {
            name: "ref",
            description: (
              <span>
                The ref you have passed. If missing, <code>null</code>
              </span>
            ),
          },
          {
            name: "props",
            description: "The props you have passed except for ref and key",
          },
          {
            name: "key",
            description: (
              <span>
                The key you have passed, coerced to a string. If missing,{" "}
                <code>null</code>
              </span>
            ),
          },
        ],
      },
    },
  ],
]);

export const elementHelper = new Map([
  ["isValidElement", { type: "legacy", scale: 0.3 }],
  ["cloneElement", { type: "legacy", scale: 0.3 }],
]);

export const classComponents = new Map([
  ["Component", { type: "legacy", scale: 0.3 }],
  ["PureComponent", { type: "legacy", scale: 0.1 }],
  ["createRef", { type: "legacy", scale: 0.1 }],
]);

export const children = new Map([
  ["Children.map()", { type: "legacy", scale: 0.1 }],
  ["Children.forEach()", { type: "legacy", scale: 0.1 }],
  ["Children.toArray()", { type: "legacy", scale: 0.1 }],
  ["Children.only()", { type: "legacy", scale: 0.3 }],
  ["Children.count()", { type: "legacy", scale: 0.1 }],
]);

export const devTools = new Map([
  [
    "StrictMode",
    {
      type: "component",
      scale: 0.8,
      description: (
        <span>
          Use <code>StrictMode</code> to enable additional development behaviors
          and warnings for the entire component tree inside. To help you find
          accidentally impure code, Strict Mode calls some of your functions
          (only the ones that should be pure) twice in development.
        </span>
      ),
      parameters: [
        {
          name: "children",
          metatype: "ReactNode",
          description:
            "The UI for the app or the part of the app you want to enable StrictMode for.",
        },
      ],
      recommended: [],
      return: null,
      docs: [
        {
          title:
            "Keeping Components Pure — Detecting impure calculations with StrictMode",
          url: "https://beta.reactjs.org/learn/keeping-components-pure#detecting-impure-calculations-with-strict-mode",
        },
      ],
    },
  ],
  [
    "Profiler",
    {
      type: "component",
      description: (
        <span>
          Wrap a component tree in a <code>&lt;Profiler&gt;</code> to measure
          its rendering performance.
        </span>
      ),
      scale: 0.5,
      docs: [
        {
          title: "Introducing the React Profiler",
          url: "https://reactjs.org/blog/2018/09/10/introducing-the-react-profiler.html",
        },
      ],
      recommended: [
        {
          title: "React DevTools Tutorial",
          url: "https://react-devtools-tutorial.vercel.app/",
        },
      ],
      parameters: [
        {
          name: "id",
          value: "name",
          description:
            "A string identifying the part of the UI you are measuring.",
          type: "string",
        },
        {
          name: "onRender",
          description:
            "A callback that React calls it every time components within the profiled tree update. It receives information about what was rendered and how much time it took.",
          type: "function",
          value: "onRender",
          parameters: [
            {
              name: "id",
              type: "string",
              description: (
                <span>
                  The string id prop of the <code>&lt;Profiler&gt;</code> tree
                  that has just committed. This lets you identify which part of
                  the tree was committed if you are using multiple profilers.
                </span>
              ),
            },
            {
              name: "phase",
              description:
                "This lets you know whether the tree has just been mounted for the first time or re-rendered due to a change in props, state, or hooks.",
              type: "'mount' | 'update'",
            },
            {
              name: "actualDuration",
              type: "number",
              description: (
                <code>
                  "The number of milliseconds spent rendering the{" "}
                  <code>&lt;Profiler&gt;</code> and its descendants for the
                  current update. This indicates how well the subtree makes use
                  of memoization (e.g. <code>memo</code> and{" "}
                  <code>useMemo</code>). Ideally this value should decrease
                  significantly after the initial mount as many of the
                  descendants will only need to re-render if their specific
                  props change.
                </code>
              ),
            },
            {
              name: "baseDuration",
              type: "number",
              description: (
                <code>
                  The number of milliseconds estimating how much time it would
                  take to re-render the entire <code>&lt;Profiler&gt;</code>{" "}
                  subtree without any optimizations. It is calculated by summing
                  up the most recent render durations of each component in the
                  tree. This value estimates a worst-case cost of rendering
                  (e.g. the initial mount or a tree with no memoization).
                  Compare <code>actualDuration</code> against it to see if
                  memoization is working.
                </code>
              ),
            },
            {
              name: "startTime",
              type: "number",
              description:
                "A numeric timestamp for when React began rendering the current update.",
            },
            {
              name: "endTime",
              type: "number",
              description:
                "A numeric timestamp for when React committed the current update. This value is shared between all profilers in a commit, enabling them to be grouped if desirable.",
            },
          ],
          return: undefined,
        },
        {
          name: "children",
          description:
            "The part of the tree of which you want to measure the cost of rendering",
        },
      ],
    },
  ],
  [
    "useDebugValue",
    {
      type: "function",
      metatype: "hook",
      scale: 0.5,
      recommended: [],
      parameters: [
        {
          name: "value",
          description: "The value you want to display in React DevTools.",
          type: "any",
        },
        {
          name: "format",
          optional: true,
          type: "function",
          parameters: [
            {
              name: "value",
              type: "any",
              description:
                "The value you want to format for display in React DevTools",
            },
          ], // again refer to prev param
          description:
            "In some cases formatting a value for display might be an expensive operation. It’s also unnecessary unless a Hook is actually inspected. For this reason, useDebugValue accepts a formatting function as an optional second parameter. This function is only called if the Hooks are inspected. It receives the debug value as a parameter and should return a formatted display value. When the component is inspected, React DevTools will call the formatting function with the value as the argument, and then display the returned formatted value.",
        },
      ],
    },
  ],
]);

export const devToolsPosition = {
  height: 205,
  width: elementWidth * 2,
  x: hooksRectWidth - elementWidth,
  y: hooksRectHeight - 220,
};

export const context = new Map([
  [
    "useContext",
    {
      type: "function",
      metatype: "hook",
      scale: 0.9,
      description:
        "Lets you read and subscribe to context from your component.",
      julesNote: (
        <span>
          Context is often mentioned along with state and gets called ”a state
          management tool”. It’s not the right way to think about it. Think of
          Context values as implicit props. Its <code>value</code> is like a
          prop passed to each component within its tree, you can opt in to read
          it with <code>useContext()</code>.
        </span>
      ),
      return: {
        name: "value",
        type: "any", // some real advanced shit would be if you could dynamically add the type in the map and spread it like TS would.
        description: (
          <span>
            The context value for the calling component. It is determined as the
            value passed to the closest <code>SomeContext.Provider</code> above
            the calling component in the tree. If there is no such provider,
            then the returned value will be the <code>defaultValue</code> you
            have passed to <code>createContext</code> for that context. The
            returned value is always up-to-date. React automatically re-renders
            components that read some context if it changes.
          </span>
        ),
      },
      parameters: [
        {
          name: "SomeContext",
          type: "object",
          metatype: "Context object",
          description: (
            <span>
              The context that you’ve previously created with{" "}
              <code>createContext</code>. The context itself does not hold the
              information, it only represents the kind of information you can
              provide or read from components.
            </span>
          ),
        },
      ],
      recommended: [
        {
          title: "How to use React Context effectively",
          url: "https://kentcdodds.com/blog/how-to-use-react-context-effectively",
        },
        {
          url: "https://blog.thoughtspile.tech/2021/10/27/better-react-context/",
          title:
            "Why I always wrap Context.Provider and useContext | Thoughtspile",
        },
      ],
      docs: [
        {
          url: "https://beta.reactjs.org/learn/passing-data-deeply-with-context",
          title: "Passing Data Deeply with Context",
        },
        {
          url: "https://beta.reactjs.org/learn/scaling-up-with-reducer-and-context",
          title: "Scaling Up with Reducer and Context",
        },
      ],
    },
  ],
  [
    "createContext",
    {
      type: "function",
      metatype: "api",
      scale: 0.9,
      description: (
        <span>
          Lets you create a context that components can provide or read. Call{" "}
          <code>createContext</code> outside of any components to create a
          context.
        </span>
      ),
      parameters: [
        {
          name: "defaultValue",
          type: "any",
          description:
            "The value that you want the context to have when there is no matching context provider in the tree above the component that reads context.",
        },
      ],
      return: {
        type: "object",
        metatype: "Context object",
        name: "SomeContext",
        description: (
          <span>
            The context object itself does not hold any information. It
            represents which context other components can read or provide.
            Typically, you will use <code>SomeContext.Provider</code> in
            components above to specify the context value, and call{" "}
            <code>useContext(SomeContext)</code> in components below to read it
          </span>
        ),
        properties: [
          {
            name: "Provider",
            type: "component",
            description:
              "Wrap your components into a context provider to specify the value of this context for all components inside.",
            parameters: [
              {
                name: "value",
                type: "any",
                description: (
                  <span>
                    The value that you want to pass to all the components
                    reading this context inside this provider, no matter how
                    deep. The context value can be of any type. A component
                    calling <code>useContext(SomeContext)</code> inside of the
                    provider receives the value of the innermost corresponding
                    context provider above it.
                  </span>
                ),
              },
            ],
          },
          {
            name: "displayName",
            type: "string",
            description:
              "A string property that the React DevTools uses to to determine what to display for the context.",
          },
          {
            name: "Consumer",
            type: "component",
            legacy: "true",
            description: (
              <span>
                Before <code>useContext</code> existed,{" "}
                <code>Context.Consumer</code> was used to read context. Although
                this older way still works, newly written code should read
                context with <code>useContext()</code> instead.
              </span>
            ),
            parameters: [
              {
                name: "children",
                type: "function",
                description: (
                  <span>
                    React will call the function you pass with the current
                    context value determined by the same algorithm as{" "}
                    <code>useContext()</code> does, and render the result you
                    return from this function. React will also re-run this
                    function and update the UI whenever the context passed from
                    the parent components have changed.
                  </span>
                ),
              },
            ],
          },
        ],
      },
      recommended: [
        {
          url: "https://blog.thoughtspile.tech/2021/10/27/better-react-context/",
          title:
            "Why I always wrap Context.Provider and useContext | Thoughtspile",
        },
        {
          title: "How to use React Context effectively",
          url: "https://kentcdodds.com/blog/how-to-use-react-context-effectively",
        },
        {
          url: "https://julesblom.com/writing/component-displayname",
          title: "Avoid Anonymous Components With the displayName Property",
        },
      ],
    },
  ],
]);

export const contextPosition = {
  height: 2 * partHeight + 20,
  width: elementWidth,
  x: hooksRectWidth / 2 - elementWidth / 2,
  y: -77,
};

export const effects = new Map([
  [
    "useEffect",
    {
      description: "Lets you synchronize a component with an external system.",
      type: "function",
      scale: 0.9,
      metatype: "hook",
      julesNote:
        "If you’re using it a whole lot, you’re probably using it wrong.",
      return: null,
      parameters: [
        {
          name: "setup",
          type: "function",
          metatype: "setup",
          parameters: [],
          return: undefined,
          description:
            "The function with your Effect’s logic. Your setup function may also optionally return a cleanup function. When your component is first added to the DOM, React will run your setup function. After every re-render with changed dependencies, React will first run the cleanup function (if you provided it) with the old values, and then run your setup function with the new values. After your component is removed from the DOM, React will run your cleanup function one last time.",
        },
        {
          name: "dependencies",
          optional: true,
          description:
            "The list of all reactive values referenced inside of the setup code. If you don’t specify the dependencies at all, your Effect will re-run after every re-render of the component.",
          type: "array",
          metatype: "dependency list",
        },
      ],
      docs: [
        {
          url: "https://beta.reactjs.org/learn/synchronizing-with-effects",
          title: "Synchronizing with Effects",
        },
        {
          url: "https://beta.reactjs.org/learn/you-might-not-need-an-effect",
          title: "You Might Not Need an Effect",
        },
        {
          url: "https://beta.reactjs.org/learn/lifecycle-of-reactive-effects",
          title: "Lifecycle of Reactive Effects",
        },
        {
          url: "https://beta.reactjs.org/learn/separating-events-from-effects",
          title: "Separating Events from Effects",
        },
        {
          title: "Removing Effect Dependencies",
          url: "https://beta.reactjs.org/learn/removing-effect-dependencies",
        },
      ],
      recommended: [
        {
          url: "https://overreacted.io/a-complete-guide-to-useeffect/",
          title: "A Complete Guide to useEffect — Overreacted",
        },
        {
          url: "https://tkdodo.eu/blog/simplifying-use-effect",
          title: "Simplifying useEffect | TkDodo's blog",
        },
        {
          url: "https://epicreact.dev/myths-about-useeffect/",
          title: "Myths about useEffect | Epic React",
        },
        {
          url: "https://reacttraining.com/blog/useEffect-is-not-the-new-componentDidMount/",
          title:
            "React Training: useEffect(fn, []) is not the new componentDidMount()",
        },
      ],
    },
  ],
  [
    "useLayoutEffect",
    {
      type: "function",
      scale: 0.5,
      metatype: "hook",
      description:
        "A version of useEffect that fires before the browser repaints the screen",
      return: null,
      parameters: [
        {
          name: "setup",
          type: "function",
          metatype: "setup",
          parameters: [],
          return: undefined,
          description:
            "The function with your Effect’s logic. Your setup function may also optionally return a cleanup function. Before your component is first added to the DOM, React will run your setup function. After every re-render with changed dependencies, React will first run the cleanup function (if you provided it) with the old values, and then run your setup function with the new values. Before your component is removed from the DOM, React will run your cleanup function one last time.",
        },
        {
          name: "dependencies",
          optional: true,
          type: "array",
          metatype: "dependency list",
          description:
            "The list of all reactive values referenced inside of the setup code. Reactive values include props, state, and all the variables and functions declared directly inside your component body.",
        },
      ],
      recommended: [
        {
          title: "useEffect vs useLayoutEffect",
          url: "https://kentcdodds.com/blog/useeffect-vs-uselayouteffect",
        },
        {
          title: "useLayoutEffect and server rendering",
          url: "https://gist.github.com/gaearon/e7d97cdf38a2907924ea12e4ebdf3c85",
        },
        {
          title: "When to useLayoutEffect Instead of useEffect (example)",
          url: "https://daveceddia.com/useeffect-vs-uselayouteffect/",
        },
        {
          title: "Hooks You Probably Don't Need — useLayoutEffect",
          url: "https://reacttraining.com/blog/hooks-you-probably-dont-need#uselayouteffect",
        },
      ],
    },
  ],
  [
    "useInsertionEffect",
    {
      type: "function",
      scale: 0.1,
      metatype: "hook",
      description:
        "A version of useEffect that fires before the browser repaints the screen",
      recommended: [],
      library: true,
      return: null,
      parameters: [
        {
          name: "setup",
          type: "function",
          metatype: "setup",
          parameters: [],
          return: undefined,
          description:
            "The function with your Effect’s logic. Your setup function may also optionally return a cleanup function. Before your component is first added to the DOM, React will run your setup function. After every re-render with changed dependencies, React will first run the cleanup function (if you provided it) with the old values, and then run your setup function with the new values. Before your component is removed from the DOM, React will run your cleanup function one last time.",
        },
        {
          name: "dependencies",
          description:
            "The list of all reactive values referenced inside of the setup code.",
          type: "array",
          metatype: "dependency list",
        },
      ],
    },
  ],
]);

export const effectsPosition = {
  height: effects.size * partHeight,
  width: elementWidth,
  x: 70,
  y: 100,
};

export const memoization = new Map([
  [
    "memo",
    {
      type: "function",
      metatype: "api",
      scale: 0.6,
      description:
        "Lets you skip re-rendering a component when its props are unchanged",
      return: {
        name: "MemoizedComponent",
        type: "component",
        description: (
          <span>
            A component that behaves the same as the component provided to{" "}
            <code>memo</code> except that React will not always re-render it
            when its parent is being re-rendered unless its props have changed
          </span>
        ),
      },
      recommended: [
        {
          title: "Before You memo() — Overreacted",
          url: "https://overreacted.io/before-you-memo/",
        },
        {
          title:
            "Preemptive memoization in React is probably not Evil (yet) - Zhenghao.io",
          url: "https://www.zhenghao.io/posts/memo-or-not",
        },
        {
          title: "One simple trick to optimize React re-renders",
          url: "https://kentcdodds.com/blog/optimize-react-re-renders",
        },
        {
          url: "https://julesblom.com/writing/component-displayname",
          title: "Avoid Anonymous Components With the displayName Property",
        },
      ],
      parameters: [
        {
          name: "SomeComponent",
          type: "component",
          description: (
            <span>
              The component that you want to memoize. The memo does not modify
              this component, but returns a new, memoized component instead. Any
              valid React component, including functions and{" "}
              <code>forwardRef</code> components, is accepted.
            </span>
          ),
        },
        {
          name: "arePropsEqual",
          description: (
            <>
              <p>
                A function that accepts two arguments: the component’s previous
                props, and its new props. It should return true if the old and
                new props are equal: that is, if the component will render the
                same output and behave in the same way with the new props as
                with the old. Otherwise it should return <code>false</code>.
              </p>
              <p>
                If you provide a custom <code>arePropsEqual</code>{" "}
                implementation, you <em>must</em> compare every prop, including
                functions. Do not rely on it to “prevent” a render, as this can
                lead to bugs.
              </p>
            </>
          ),
          julesNote:
            "Don’t abuse this! For example skipping functions in the compare function can lead to stale UI and hard-to-find bugs.",
          type: "function",
          parameters: [
            {
              name: "oldProps",
              type: "object",
              metatype: "props",
              description: "The component’s props of the current render",
            },
            {
              name: "newProps",
              type: "object",
              metatype: "props",
              description: "The component’s props of the previous render",
            },
          ],
          return: { name: "boolean", type: "boolean" },
          optional: true,
        },
      ],
    },
  ],
  [
    "useMemo",
    {
      description:
        "Lets you cache the result of a calculation between re-renders.",
      scale: 0.8,
      return: {
        name: "cachedValue",
        description: (
          <span>
            On the initial render, <code>useMemo</code> returns the result of
            calling <code>calculateValue</code> with no arguments. During
            subsequent renders, it will either return an already stored value
            from the last render (if the dependencies haven’t changed), or call{" "}
            <code>calculateValue</code> again, and return the result that{" "}
            <code>calculateValue</code> has returned.
          </span>
        ),
        type: "any",
      },
      type: "function",
      metatype: "hook",
      parameters: [
        {
          name: "calculateValue",
          type: "function",
          metatype: "pure",
          description: (
            <span>
              The function calculating the value that you want to cache. It
              should be pure, should take no arguments, and should return a
              value of any type. React will call your function during the
              initial render. On subsequent renders, React will return the same
              value again if the dependencies have not changed since the last
              render. Otherwise, it will call <code>calculateValue</code>,
              return its result, and store it in case it can be reused later
            </span>
          ),
          parameters: [],
          return: {
            type: "any",
            name: "valueToMemoize",
          },
        },
        {
          name: "dependencies",
          type: "array",
          metatype: "dependency list",
          description: (
            <span>
              The list of all reactive values referenced inside of the{" "}
              <code>calculateValue</code> code. Reactive values include props,
              state, and all the variables and functions declared directly
              inside your component body.
            </span>
          ),
        },
      ],
      recommended: [
        {
          url: "https://www.chakshunyu.com/blog/a-deep-dive-comparison-between-usememo-and-usecallback/",
          title:
            "A Deep Dive Comparison Between useMemo And useCallback | Chak Shun Yu",
        },
        {
          title: "Understanding useMemo and useCallback | Josh W Comeau",
          url: "https://www.joshwcomeau.com/react/usememo-and-usecallback/",
        },
        {
          title: "How useRef turned out to be useMemo's father | Thoughtspile",
          url: "https://blog.thoughtspile.tech/2021/04/05/useref-usememo/",
        },
        {
          title:
            "Preemptive memoization in React is probably not Evil (yet) - Zhenghao.io",
          url: "https://www.zhenghao.io/posts/memo-or-not",
        },
      ],
    },
  ],
  [
    "useCallback",
    {
      type: "function",
      scale: 0.8,
      metatype: "hook",
      description: "Lets you cache a function definition between re-renders.",
      return: {
        name: "cachedFn",
        type: "function",
        description: (
          <span>
            On the initial render, <code>useCallback</code> returns the{" "}
            <code>fn</code> function you have passed. During subsequent renders,
            it will either return an already stored <code>fn</code> function
            from the last render (if the dependencies haven’t changed), or
            return the <code>fn</code> function you have passed during this
            render.
          </span>
        ),
      },
      parameters: [
        {
          name: "fn",
          type: "function",
          return: {
            name: "any",
            type: "any",
          },
          parameters: [{ name: "...params" }],
          description:
            "The function value that you want to cache. It can take any arguments and return any values. React will return (not call!) your function back to you during the initial render. On subsequent renders, React will give you the same function again if the dependencies have not changed since the last render. Otherwise, it will give you the function that you have passed during the current render, and store it in case it can be reused later. React will not call your function. The function is returned to you so you can decide when and whether to call it.",
        },
        {
          name: "dependencies",
          type: "array",
          metatype: "dependency list",
          description: (
            <span>
              The list of all reactive values referenced inside of the{" "}
              <code>fn</code> code. Reactive values include props, state, and
              all the variables and functions declared directly inside your
              component body.
            </span>
          ),
        },
      ],
      recommended: [
        {
          url: "https://www.chakshunyu.com/blog/a-deep-dive-comparison-between-usememo-and-usecallback/",
          title:
            "A Deep Dive Comparison Between useMemo And useCallback | Chak Shun Yu",
        },
        {
          title: "Understanding useMemo and useCallback | Josh W Comeau",
          url: "https://www.joshwcomeau.com/react/usememo-and-usecallback/",
        },
        {
          url: "https://royi-codes.vercel.app/thousand-usecallbacks/",
          title: "Death by a thousand useCallbacks | Royi Codes",
        },
      ],
    },
  ],
]);

export const memoizationPosition = {
  height: 205,
  width: elementWidth * 2,
  x: hooksRectWidth - elementWidth,
  y: hooksRectHeight / 2.75,
};

export const refsPosition = {
  height: 205,
  width: elementWidth * 2,
  x: hooksRectWidth - elementWidth,
  y: 15,
};

export const refs = new Map([
  [
    "useRef",
    {
      type: "function",
      scale: 0.8,
      metatype: "hook",
      description: "Lets you reference a value that’s not needed for rendering",
      julesNote:
        "You use useRef whenever you want to keep track of a value, but not trigger a re-render when you update it.",
      return: {
        name: "ref",
        description: (
          <span>
            <code>useRef</code> returns an object with a single property:
          </span>
        ),
        type: "object",
        metatype: "ref object",
        properties: [
          {
            name: "current",
            type: "any",
            description: (
              <span>
                Initially, it’s set to the <code>initialValue</code> you have
                passed. You can later set it to something else. If you pass the
                ref object to React as a ref attribute to a JSX node, React will
                set it to this property
              </span>
            ),
          },
        ],
      }, // useRef returns an object with a single property: current: Initially, it’s set to the initialValue you have passed. You can later set it to something else. If you pass the ref object to React as a ref attribute to a JSX node, React will set its current property.
      parameters: [
        {
          name: "initialValue",
          type: "any",
          description:
            "The value you want the ref object’s current property to be initially. This argument is ignored after the initial render.",
        },
      ],
      docs: [
        {
          url: "https://beta.reactjs.org/learn/referencing-values-with-refs",
          title: "Referencing Values with Refs",
        },
        {
          title: "Manipulating the DOM with Refs",
          url: "https://beta.reactjs.org/learn/manipulating-the-dom-with-refs",
        },
      ],
      recommended: [
        {
          url: "https://www.developerway.com/posts/refs-from-dom-to-api",
          title: "Developer way | Refs in React: from access to DOM to imperative API",
        },
        {
          url: "https://tkdodo.eu/blog/refs-events-and-escape-hatches",
          title: "Refs, Events and Escape Hatches | TkDodo’s blog",
        },
        {
          url: "https://blog.thoughtspile.tech/2021/05/17/everything-about-react-refs/",
          title:
            "So you think you know everything about React refs | Thoughtspile",
        },
        {
          url: "https://overreacted.io/making-setinterval-declarative-with-react-hooks/",
          title:
            "Making setInterval Declarative with React Hooks — Overreacted",
        },
      ],
    },
  ],
  [
    "useImperativeHandle",
    {
      description: (
        <span>
          Lets you customize the handle exposed as a ref. It’s always used in
          combination with <code>forwardRef</code>
        </span>
      ),
      scale: 0.3,
      type: "function",
      metatype: "hook",
      // ace marke https://www.reddit.com/r/reactjs/comments/dfyclo/comment/f39w4tv/?context=3
      julesNote:
        "It's basically a way for function components to expose 'instance methods' even though they don't have instances.",
      recommended: [
        {
          title:
            "Four ways to use a fallback ref with `React.forwardRef` – Matias Kinnunen",
          url: "https://mtsknn.fi/blog/react-fallback-ref/#useimperativehandle",
        },
        {
          title: "React Hooks Explained: useImperativeHandle",
          url: "https://dev.to/anikcreative/react-hooks-explained-useimperativehandle-5g44",
        },
        {
          url: "https://www.developerway.com/posts/refs-from-dom-to-api",
          title:
            "Developer way | Refs in React: from access to DOM to imperative API",
        },
        {
          url: "https://prateeksurana.me/blog/fine-tuning-refs-with-useimperativehandle",
          title: "Prateek Surana | Fine-tuning refs with useImperativeHandle",
        },
      ],
      parameters: [
        {
          name: "ref",
          type: "object",
          metatype: "ref object",
          description: (
            <span>
              The ref you received as the second argument from the{" "}
              <code>forwardRef</code> render function.
            </span>
          ),
        },
        {
          name: "createHandle",
          type: "function",
          return: { name: "ref", type: "object" },
          parameters: [],
          description:
            "A function that takes no arguments and returns the ref handle you want to expose. The ref handle you return can have any type. Usually, you will return an object with the methods you want to expose.",
        },
        {
          name: "dependencies",
          type: "array",
          metatype: "dependency list",
          optional: true,
          description: (
            <span>
              The list of all reactive values referenced inside of the{" "}
              <code>createHandle</code> code
            </span>
          ),
        },
      ],
      return: undefined,
    },
  ],
  [
    "forwardRef",
    {
      type: "function",
      metatype: "api",
      scale: 0.4,
      description: (
        <span>
          <code>forwardRef</code> lets your component expose a DOM node to
          parent component with a ref.
        </span>
      ),
      recommended: [
        {
          title:
            "Four ways to use a fallback ref with `React.forwardRef` – Matias Kinnunen",
          url: "https://mtsknn.fi/blog/react-fallback-ref/#useimperativehandle",
        },
        {
          url: "https://julesblom.com/writing/component-displayname",
          title: "Avoid Anonymous Components With the displayName Property",
        },
      ],
      parameters: [
        {
          name: "render",
          description: (
            <span>
              The render function for your component. React calls this function
              with the props and <code>ref</code> that your component received
              from its parent. The JSX you return will be the output of your
              component.
            </span>
          ),
          type: "function",
          return: { name: "SomeComponent", type: "component" },
          parameters: [
            {
              name: "props",
              description: "The props passed by the parent component",
            },
            {
              name: "ref",
              description: "The ref attribute passed by the parent component",
            },
          ],
        },
      ],
      return: {
        type: "component",
        description: (
          <span>
            <code>forwardRef</code> returns a React component that you can
            render in JSX. Unlike React components defined as plain functions, a
            component returned by <code>forwardRef</code> is also able to
            receive a ref prop.
          </span>
        ),
        name: "SomeComponent",
      },
    },
  ],
]);

export const ssrPosition = {
  height: 145,
  width: elementWidth,
  x: 70,
  y: hooksRectHeight - 280,
};

export const ssr = new Map([
  [
    "useId",
    {
      name: "useId",
      position: ssrPosition,
      scale: 0.35,
      type: "function",
      metatype: "hook",
      description: (
        <span>
          <code>useId</code> lets you generate unique IDs that can be passed to
          accessibility attributes.
        </span>
      ),
      parameters: [],
      return: {
        type: "string",
        description: (
          <span>
            <code>useId</code> returns a unique ID string associated with this
            particular <code>useId</code> call in this particular component
          </span>
        ),
        name: "id",
      },
      recommended: [
        {
          url: "https://github.com/reactwg/react-18/discussions/111",
          title: "React 18 Working Group: Intent to Ship: useId",
        },
      ],
    },
  ],
]);

export const all = [
  effects,
  state,
  refs,
  context,
  devTools,
  memoization,
  suspense,
  elementHelper,
  classComponents,
  nodes,
  children,
  ssr,
]
  .map((m) => Array.from(m.entries()))
  .flat()
  .map(([n, { type, metatype }]) => ({ name: n, type, metatype }));

const external = new Map([
  [
    "eslint-plugin-react-hooks",
    {
      type: "ESLint plugin",
      description:
        "This is an ESLint plugin by the React core team to help you adhere to the rules of hooks. It’s a must have for any React application",
    },
  ],
  [
    "eslint-plugin-react",
    {
      type: "ESLint plugin",
      url: "https://github.com/jsx-eslint/eslint-plugin-react",
      description:
        "This is a third party plugin. I personally don’t like the recommended config. I recommend to go through the rules and decide what you want to enable or use `eslint-config-react-app`, which included the most sensible rules from this package and `eslint-plugin-react-hooks`.",
    },
  ],
  [
    "React Developer Tools",
    {
      type: "Browser Extension",
      url: "https://beta.reactjs.org/learn/react-developer-tools",
    },
  ],
]);
